import { useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { EntityWithNotes } from '@atlassian/cssxp-types-audit';
import { InvoiceWithLineItems } from '@atlassian/cssxp-types-billing';
import { removeContentScrollingHack, ENABLE_RECENTLY_VISITED_HISTORY } from '@atlassian/cssxp-utils-generic';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { AllTasksList, SIDEBAR_TASK_DETAIL, TaskDetailPage } from '@css-xp/apd-task-manager';
import {
  BillingDocPageV2,
  ErrorComponents,
  useFeatureToggle,
  useRecentlyViewed,
  useTitle,
  withApollo,
} from '@css-xp/base';
import { ViewPricing } from '@css-xp/base/common-sidebars';
import {
  getInvoiceActions,
  getInvoiceAdditionalDetails,
  getInvoiceBreadcrumbTrail,
  getInvoiceHeader,
  getInvoiceSubheader,
} from '../invoice-page-utils';
import { fetchInvoiceQueryV2 } from '../queries/fetch-invoice-query';
import { InvoiceRefundCcpPane } from '../sidebar/invoice-refund-ccp-pane';
import { CancelInvoicePanel } from './cancel-invoice';
import InvoiceLineItems from './invoice-line-items';
import InvoicePdfLinkPane from './invoice-pdf-link-pane';
import InvoiceUncollectiblePane from './invoice-uncollectible-pane';
import InvoiceUpdatePane from './invoice-update-pane';
import InvoiceViewAdjustmentPane from './invoice-view-adjustments-pane';
import SelfServeBACInvoiceLink from './self-serve-bac-invoice-link-pane';

export enum SIDEBAR {
  PDF_LINK_PANE = 'pdf-link-pane',
  UPDATE_MEMO_PANE = 'update-memo-pane',
  RESENT_INVOICE_PANE = 'resend-invoice-pane',
  PROCESS_REFUND_CCP = 'process-refund-ccp',
  INVOICE_VIEW_ADJUSTMENTS = 'invoice-view-adjustments',
  UNCOLLECTIBLE_PANE = 'uncollectible-pane',
  CANCEL_INVOICE = 'cancel-invoice',
  VIEW_PRICING_DETAILS = 'viewPricingDetails',
  SELF_SERVE_BAC_INVOICE_LINK = 'self-serve-bac-invoice-link',
  INCLUDED_PRODUCTS = 'included-products',
}

const InvoicePage = ({ appPageClass, title }: { appPageClass?: string; title?: string }): JSX.Element | null => {
  useEffect(() => {
    removeContentScrollingHack();
  }, []);

  const { id } = useParams<{ id: string }>();
  const [isEnabled] = useFeatureToggle();
  useTitle(id, title);
  // TODO:  Need to update it further for presence hook but using redux logic once user profile service done
  //const { viewers } = usePresence('invoice', id);
  //const previousId = usePrevious(id);
  const { data, loading, error, refetch } = useQuery(fetchInvoiceQueryV2, {
    variables: {
      id,
    },
    fetchPolicy: 'no-cache',
  });
  const { updateRecentlyViewed } = useRecentlyViewed();
  React.useEffect(() => {
    if (data && isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
      updateRecentlyViewed({
        entityName: title?.toLowerCase().trim().replace(/\s+/g, '_'),
        metadata: {
          invoiceNumber: data.invoice.invoiceNumber,
          fullName: data.invoice.billTo.fullName,
        },
      });
    }
  }, [data, title]);

  // to capture tha page view experience for invoice page
  const name = window.location.pathname?.split('/')[1];
  GlobalPageLoadExperience.startPageLoad(name);
  React.useEffect(() => {
    GlobalPageLoadExperience.success({ force: true });
  }, []);

  if (loading) return <LoadingIndicator />;

  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  if (!data) {
    return (
      <ErrorComponents.ErrorPanel error={new ErrorComponents.NotFoundError(`Unable to find invoice with ID: ${id}`)} />
    );
  }

  const invoice = data.invoice as InvoiceWithLineItems;
  const auditTrailProps = {
    entityName: EntityWithNotes.INVOICE,
    id: invoice?.id,
    idText: 'credit-note',
  };

  const allowedSidebars = {
    [SIDEBAR.PDF_LINK_PANE]: InvoicePdfLinkPane,
    [SIDEBAR.UPDATE_MEMO_PANE]: InvoiceUpdatePane,
    [SIDEBAR.RESENT_INVOICE_PANE]: InvoiceUpdatePane,
    [SIDEBAR.PROCESS_REFUND_CCP]: InvoiceRefundCcpPane,
    [SIDEBAR.INVOICE_VIEW_ADJUSTMENTS]: InvoiceViewAdjustmentPane,
    [SIDEBAR.UNCOLLECTIBLE_PANE]: InvoiceUncollectiblePane,
    [SIDEBAR_TASK_DETAIL]: TaskDetailPage,
    [SIDEBAR.CANCEL_INVOICE]: CancelInvoicePanel,
    [SIDEBAR.VIEW_PRICING_DETAILS]: withApollo(ViewPricing),
    [SIDEBAR.SELF_SERVE_BAC_INVOICE_LINK]: SelfServeBACInvoiceLink,
  };

  return (
    <BillingDocPageV2
      headerProps={{
        getSubheader: getInvoiceSubheader,
        getHeader: getInvoiceHeader,
        getActions: getInvoiceActions,
        //viewers,
      }}
      onComplete={refetch}
      billingDoc={invoice}
      isFetchingBillingDoc={false}
      billingDocFetchError={null}
      getBreadcrumbTrail={getInvoiceBreadcrumbTrail}
      auditTrailProps={auditTrailProps}
      analyticsScreenSource={AnalyticsEventSource.INVOICE_SCREEN}
      additionalDetailsContent={getInvoiceAdditionalDetails(invoice)}
      BillingDocLineItems={InvoiceLineItems}
      allowedSidebars={allowedSidebars}
      paymentRetry={invoice.dunningStatus}
      approvalSection={<AllTasksList businessKey={invoice.invoiceNumber ?? undefined} searchable={false} />}
      appPageClass={appPageClass + ' invoice-page invoice-page-v2'}
    />
  );
};

export default InvoicePage;
